import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Mic, Square, Play, Trash2, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface AudioRecorderProps {
  onRecordingComplete: (file: File) => void;
  onCancel: () => void;
}

export function AudioRecorder({ onRecordingComplete, onCancel }: AudioRecorderProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [audioURL, setAudioURL] = useState<string | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm;codecs=opus' });
        const url = URL.createObjectURL(audioBlob);
        setAudioURL(url);
        
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error("Erro ao acessar microfone:", error);
      toast.error("Não foi possível acessar o microfone. Verifique as permissões.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const deleteRecording = () => {
    setAudioURL(null);
    setRecordingTime(0);
    audioChunksRef.current = [];
  };

  const playRecording = () => {
    if (audioURL && audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const confirmRecording = () => {
    if (!audioURL) return;

    const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm;codecs=opus' });
    
    // ⭐ ADICIONAR DATA ATUAL NOS METADADOS
    const now = new Date();
    const dateString = now.toISOString().split('T')[0]; // YYYY-MM-DD
    
    const file = new File(
      [audioBlob],
      `audio_recording_${Date.now()}.webm`,
      { 
        type: 'audio/webm',
        lastModified: now.getTime() // Timestamp atual
      }
    );

    console.log('📅 Audio file created with date:', dateString);
    onRecordingComplete(file);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex flex-col items-center w-full px-4 sm:px-6 py-4 gap-4">
      <Card className="w-full max-w-md border-primary rounded-xl shadow-sm">
        <CardContent className="pt-6 px-4 sm:px-6">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col items-center justify-center py-8 sm:py-12 bg-muted rounded-xl">
              <div className={`relative ${isRecording ? 'animate-pulse' : ''}`}>
                <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-primary flex items-center justify-center">
                  <Mic className="h-8 w-8 sm:h-10 sm:w-10 text-primary-foreground" />
                </div>
                {isRecording && (
                  <div className="absolute inset-0 rounded-full border-4 border-primary/30 animate-ping" />
                )}
              </div>
              
              <div className="mt-4 sm:mt-6 text-center">
                <div className="text-3xl sm:text-4xl font-mono font-bold">
                  {formatTime(recordingTime)}
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {isRecording ? "Gravando..." : audioURL ? "Gravação concluída" : "Pronto para gravar"}
                </p>
              </div>
            </div>

          <Separator className="my-4" />

          {audioURL && (
            <audio
              ref={audioRef}
              src={audioURL}
              onEnded={() => setIsPlaying(false)}
              className="hidden"
            />
          )}

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full mt-4">
            {!audioURL ? (
              <>
                <Button
                  onClick={onCancel}
                  variant="outline"
                  size="lg"
                  disabled={isRecording}
                  className="w-full sm:w-auto"
                >
                  Cancelar
                </Button>

                {!isRecording ? (
                  <Button
                    onClick={startRecording}
                    size="lg"
                    className="w-full sm:w-auto"
                  >
                    <Mic className="h-5 w-5 mr-2" />
                    Iniciar Gravação
                  </Button>
                ) : (
                  <Button
                    onClick={stopRecording}
                    size="lg"
                    variant="destructive"
                    className="w-full sm:w-auto"
                  >
                    <Square className="h-5 w-5 mr-2" />
                    Parar Gravação
                  </Button>
                )}
              </>
            ) : (
              <div className="flex flex-col gap-3 w-full">
                <Button
                  onClick={confirmRecording}
                  size="lg"
                  className="w-full bg-primary hover:bg-primary/90 shadow-md"
                >
                  <Check className="h-5 w-5 mr-2" />
                  Usar Esta Gravação
                </Button>
                
                <div className="flex gap-2">
                  <Button
                    onClick={deleteRecording}
                    variant="destructive"
                    size="sm"
                    className="flex-1"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Excluir
                  </Button>

                  <Button
                    onClick={playRecording}
                    variant="outline"
                    size="sm"
                    disabled={isPlaying}
                    className="flex-1"
                  >
                    {isPlaying ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4 mr-2" />
                    )}
                    Reproduzir
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
    </div>
  );
}
